import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtilsService } from '../../_base/crud';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth';
import { Immunization } from '../_models/immunization.model';

export enum ViewContext {
  contacts = 'CONTACT',
  patients = 'PATIENT',
}

@Injectable({
  providedIn: 'root',
})
export class ImmunizationsService {
  private authToken;
  private companyId;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  findImmunizations(entityId: number): Observable<any> {
    const params = new HttpParams({ fromString: '_patient=' + entityId + '&_count=' + '&_page=' });
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = 'https://aplos2.aidbox.app/Immunization';
    return this.http.get<any>(url, { headers, params });
  }

  createImmunization(_immunization: Immunization, entityId) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      status: _immunization.status,
      statusReason: {
        coding: [
          {
            code: _immunization.statusReason ? _immunization.statusReason[`concept`][`id`] : null,
            display: _immunization.statusReason
              ? _immunization.statusReason[`concept`][`fsn`][`term`]
              : null,
          },
        ],
      },
      vaccineCode: {
        coding: [
          {
            code: _immunization.vaccine ? _immunization.vaccine[`concept`][`id`] : null,
            display: _immunization.vaccine ? _immunization.vaccine[`concept`][`fsn`][`term`] : null,
          },
        ],
      },
      patient: {
        id: entityId.toString(),
        resourceType: 'Patient',
      },
      occurrence: {
        dateTime: _immunization.occurrence,
      },
      recorded: _immunization.recordedDate,
      primarySource: _immunization.primarySource,
      reportOrigin: {
        coding: [
          {
            code: _immunization.reportOrigin ? _immunization.reportOrigin[`code`] : null,
            display: _immunization.reportOrigin ? _immunization.reportOrigin[`display`] : null,
          },
        ],
      },
      manufacturer: {
        id: this.companyId.toString(),
        resourceType: 'Organization',
      },
      lotNumber: _immunization.lotNumber ? _immunization.lotNumber.toString() : null,
      expirationDate: _immunization.expirationDate ? _immunization.expirationDate : null,
      site: {
        coding: [
          {
            code: _immunization.site ? _immunization.site[`code`] : null,
            display: _immunization.site ? _immunization.site[`display`] : null,
          },
        ],
      },
      route: {
        coding: [
          {
            code: _immunization.route ? _immunization.route[`code`] : null,
            display: _immunization.route ? _immunization.route[`display`] : null,
          },
        ],
      },
      doseQuantity: {
        value: _immunization.doseQuantityValue,
        unit: _immunization.doseQuantityUnit,
      },
      performer: [
        {
          function: {
            coding: [
              {
                code: _immunization.function ? _immunization.function[`code`] : null,
                display: _immunization.function ? _immunization.function[`display`] : null,
              },
            ],
          },
          actor: {
            id: _immunization.actor ? _immunization.actor[`id`].toString() : null,
            resourceType: _immunization.actorType,
          },
        },
      ],
      note: [
        {
          text: _immunization.note,
        },
      ],
      reasonCode: [
        {
          coding: [
            {
              code: _immunization.reasonCode ? _immunization.reasonCode[`concept`][`id`] : null,
              display: _immunization.reasonCode
                ? _immunization.reasonCode[`concept`][`fsn`][`term`]
                : null,
            },
          ],
        },
      ],
      reasonReference: [
        {
          id: _immunization.condition ? _immunization.condition.id : null,
          resourceType: 'Condition',
        },
      ],
      isSubpotent: _immunization.isSubpotent,
      subpotentReason: [
        {
          coding: [
            {
              code: _immunization.subpotentReason ? _immunization.subpotentReason[`code`] : null,
              display: _immunization.subpotentReason
                ? _immunization.subpotentReason[`display`]
                : null,
            },
          ],
        },
      ],
      education: [
        {
          documentType: _immunization.documentType,
          reference: _immunization.reference,
          publicationDate: _immunization.publicationDate,
          presentationDate: _immunization.presentationDate,
        },
      ],
      programEligibility: [
        {
          coding: [
            {
              code: _immunization.programEligibility
                ? _immunization.programEligibility[`code`]
                : null,
              display: _immunization.programEligibility
                ? _immunization.programEligibility[`display`]
                : null,
            },
          ],
        },
      ],
      fundingSource: {
        coding: [
          {
            code: _immunization.fundingSource ? _immunization.fundingSource[`code`] : null,
            display: _immunization.fundingSource ? _immunization.fundingSource[`display`] : null,
          },
        ],
      },
      protocolApplied: [
        {
          series: _immunization.series,
          authority: {
            id: this.companyId.toString(),
            resourceType: 'Organization',
          },
          targetDisease: [
            {
              coding: [
                {
                  code: _immunization.reasonCode ? _immunization.reasonCode[`concept`][`id`] : null,
                  display: _immunization.reasonCode
                    ? _immunization.reasonCode[`concept`][`fsn`][`term`]
                    : null,
                },
              ],
            },
          ],
          doseNumber: {
            positiveInt: _immunization.doseNumber,
          },
          seriesDoses: {
            positiveInt: _immunization.seriesDoses,
          },
        },
      ],
    };

    return this.http.post<Immunization>('https://aplos2.aidbox.app/Immunization', body, {
      headers: httpHeaders,
      observe: 'response',
    });
  }

  updateImmunization(_immunization: Immunization, entityId): Observable<Immunization> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      status: _immunization.status,
      statusReason: {
        coding: [
          {
            code: _immunization.statusReason ? _immunization.statusReason[`concept`][`id`] : null,
            display: _immunization.statusReason
              ? _immunization.statusReason[`concept`][`fsn`][`term`]
              : null,
          },
        ],
      },
      vaccineCode: {
        coding: [
          {
            code: _immunization.vaccine ? _immunization.vaccine[`concept`][`id`] : null,
            display: _immunization.vaccine ? _immunization.vaccine[`concept`][`fsn`][`term`] : null,
          },
        ],
      },
      patient: {
        id: entityId.toString(),
        resourceType: 'Patient',
      },
      occurrence: {
        dateTime: _immunization.occurrence,
      },
      recorded: _immunization.recordedDate,
      primarySource: _immunization.primarySource,
      reportOrigin: {
        coding: [
          {
            code: _immunization.reportOrigin ? _immunization.reportOrigin[`code`] : null,
            display: _immunization.reportOrigin ? _immunization.reportOrigin[`display`] : null,
          },
        ],
      },
      manufacturer: {
        id: this.companyId.toString(),
        resourceType: 'Organization',
      },
      lotNumber: _immunization.lotNumber ? _immunization.lotNumber.toString() : null,
      expirationDate: _immunization.expirationDate ? _immunization.expirationDate : null,
      site: {
        coding: [
          {
            code: _immunization.site ? _immunization.site[`code`] : null,
            display: _immunization.site ? _immunization.site[`display`] : null,
          },
        ],
      },
      route: {
        coding: [
          {
            code: _immunization.route ? _immunization.route[`code`] : null,
            display: _immunization.route ? _immunization.route[`display`] : null,
          },
        ],
      },
      doseQuantity: {
        value: _immunization.doseQuantityValue,
        unit: _immunization.doseQuantityUnit,
      },
      performer: [
        {
          function: {
            coding: [
              {
                code: _immunization.function ? _immunization.function[`code`] : null,
                display: _immunization.function ? _immunization.function[`display`] : null,
              },
            ],
          },
          actor: {
            id: _immunization.actor ? _immunization.actor[`id`].toString() : null,
            resourceType: _immunization.actorType,
          },
        },
      ],
      note: [
        {
          text: _immunization.note,
        },
      ],
      reasonCode: [
        {
          coding: [
            {
              code: _immunization.reasonCode ? _immunization.reasonCode[`concept`][`id`] : null,
              display: _immunization.reasonCode
                ? _immunization.reasonCode[`concept`][`fsn`][`term`]
                : null,
            },
          ],
        },
      ],
      reasonReference: [
        {
          id: _immunization.condition ? _immunization.condition.id : null,
          resourceType: 'Condition',
        },
      ],
      isSubpotent: _immunization.isSubpotent,
      subpotentReason: [
        {
          coding: [
            {
              code: _immunization.subpotentReason ? _immunization.subpotentReason[`code`] : null,
              display: _immunization.subpotentReason
                ? _immunization.subpotentReason[`display`]
                : null,
            },
          ],
        },
      ],
      education: [
        {
          documentType: _immunization.documentType,
          reference: _immunization.reference,
          publicationDate: _immunization.publicationDate,
          presentationDate: _immunization.presentationDate,
        },
      ],
      programEligibility: [
        {
          coding: [
            {
              code: _immunization.programEligibility
                ? _immunization.programEligibility[`code`]
                : null,
              display: _immunization.programEligibility
                ? _immunization.programEligibility[`display`]
                : null,
            },
          ],
        },
      ],
      fundingSource: {
        coding: [
          {
            code: _immunization.fundingSource ? _immunization.fundingSource[`code`] : null,
            display: _immunization.fundingSource ? _immunization.fundingSource[`display`] : null,
          },
        ],
      },
      protocolApplied: [
        {
          series: _immunization.series,
          authority: {
            id: this.companyId.toString(),
            resourceType: 'Organization',
          },
          targetDisease: [
            {
              coding: [
                {
                  code: _immunization.reasonCode ? _immunization.reasonCode[`concept`][`id`] : null,
                  display: _immunization.reasonCode
                    ? _immunization.reasonCode[`concept`][`fsn`][`term`]
                    : null,
                },
              ],
            },
          ],
          doseNumber: {
            positiveInt: _immunization.doseNumber,
          },
          seriesDoses: {
            positiveInt: _immunization.seriesDoses,
          },
        },
      ],
    };

    const url = 'https://aplos2.aidbox.app/Immunization/' + _immunization.id;
    return this.http.put<Immunization>(url, body, { headers: httpHeaders });
  }

  deleteImmunization(id?: number) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>('https://aplos2.aidbox.app/Immunization/' + id, {
      headers: httpHeaders,
    });
  }
}
